import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { LearnArticleLink } from '@/features/aprender/learnArticleShared'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `news.articles.turista-gasta-menos-restaurantes-2026.${f}`

const REF = {
    egatur: {
        href: 'https://www.ine.es/dyngs/Prensa/EGATUR0726.htm',
        domain: 'ine.es',
        label: 'INE · EGATUR',
    },
    precios: {
        href: 'https://www.foodretail.es/horeca/noticias/los-precios-de-restauracion-se-mantienen-en-el-45-durante-el-mes-de-julio.html',
        domain: 'foodretail.es',
        label: 'Precios restauración · Food Retail',
    },
    hosteleriaMadrid: {
        href: 'https://www.hosteleriamadrid.com/blog/el-consumo-fuera-del-hogar-primer-trimestre-2026/',
        domain: 'hosteleriamadrid.com',
        label: 'Hostelería Madrid',
    },
    aecoc: {
        href: 'https://www.infohoreca.com/noticias/20260203/consumo-fuera-hogar-espana-aecoc-2026-gasto-hosteleria',
        domain: 'infohoreca.com',
        label: 'AECOC · InfoHoreca',
    },
    marinaAlta: {
        href: 'https://lamarina.eldiario.es/2026/09/06/la-hosteleria-de-la-marina-alta-alerta-de-un-notable-descenso-de-la-rentabilidad-de-bares-y-restaurantes-en-los-ultimos-anos/',
        domain: 'lamarina.eldiario.es',
        label: 'La Marina Plaza',
    },
    menorca: {
        href: 'https://www.menorca.info/balears/noticias/2026/08/20/2692823/turista-gasta-menos-comercio-local-llena-carritos-supermercados.html',
        domain: 'menorca.info',
        label: 'Menorca Info',
    },
    anuario: {
        href: 'https://hosteleriadeespana.es/publicaciones-hosteleria.html',
        domain: 'hosteleriadeespana.es',
        label: 'Anuario Hostelería de España',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function TuristaGastaMenosArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        {
            id: 'terraza',
            label: t(k('h2.terraza'), 'La terraza llena y la caja que no cuadra'),
        },
        {
            id: 'en-cristiano',
            label: t(k('h2.enCristiano'), 'Qué dice EGATUR de julio de 2026'),
        },
        {
            id: 'pierdes-cuota',
            label: t(k('toc.cuota'), 'Pierdes cuota en el bolsillo del turista'),
        },
        {
            id: 'cuenta-de-la-vieja',
            label: t(k('toc.dinero'), '¿A dónde se fue ese dinero?'),
        },
        {
            id: 'esta-semana',
            label: t(k('toc.octubre'), 'Qué hacer antes de octubre'),
        },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del turista y el gasto en restauración y me gustaría ver qué platos han cambiado de peso en mi carta.',
        ),
    )

    const weekActions: ReactNode[] = [
        <>
            <strong>{t(k('weekActions.1.strong'), 'Compara el mix de ventas de julio con el de julio pasado.')}</strong>
            {t(k('weekActions.1.after'), ' No la venta total, el mix: qué platos y qué bebidas han cambiado de peso.')}
        </>,
        <>
            <strong>{t(k('weekActions.2.strong'), 'Mira las comandas por separado del ticket medio.')}</strong>
            {t(
                k('weekActions.2.after'),
                ' Si las comandas aguantan y el ticket baja, no tienes un problema de afluencia y ninguna campaña lo va a arreglar.',
            )}
        </>,
        <>
            <strong>{t(k('weekActions.3.strong'), 'Escandalla los cinco platos que más han subido en el mix.')}</strong>
            {t(k('weekActions.3.after'), ' Los ha elegido el cliente, no tú, y mandan en tu food cost.')}
        </>,
        <>
            <strong>{t(k('weekActions.4.strong'), 'Revisa las bebidas aparte.')}</strong>
            {t(
                k('weekActions.4.after'),
                ' Si tu caída se parece a la de los paneles, ahí tienes el agujero de margen localizado.',
            )}
        </>,
        <>
            <strong>
                {t(k('weekActions.5.strong'), 'Antes de octubre, cierra la carta de invierno con estos números.')}
            </strong>
            {t(k('weekActions.5.after'), ' En enero ya no hay caja para probar.')}
        </>,
    ]

    const sources = [
        {
            label: t(k('sources.1'), 'INE, Encuesta de Gasto Turístico (EGATUR), julio de 2026, datos provisionales'),
            href: REF.egatur.href,
        },
        {
            label: t(
                k('sources.2'),
                'Índice de precios de restauración de julio de 2026, datos del INE recogidos por Hostelería de España',
            ),
            href: REF.precios.href,
        },
        {
            label: t(
                k('sources.3'),
                'Hostelería Madrid, consumo fuera del hogar en el primer trimestre de 2026, con datos de panel de Worldpanel by Numerator, Kantar, NIQ y MAPA',
            ),
            href: REF.hosteleriaMadrid.href,
        },
        {
            label: t(k('sources.4'), 'Barómetro AECOC 2026 sobre consumo fuera del hogar, 3 de febrero de 2026'),
            href: REF.aecoc.href,
        },
        {
            label: t(k('sources.5'), 'La Marina Plaza, declaraciones de AEHTMA, 6 de septiembre de 2026'),
            href: REF.marinaAlta.href,
        },
        {
            label: t(
                k('sources.6'),
                'Menorca Info, desvío del gasto turístico hacia grandes superficies, 20 de agosto de 2026',
            ),
            href: REF.menorca.href,
        },
        {
            label: t(k('sources.7'), 'Anuario de Hostelería de España, más de 300.000 establecimientos'),
            href: REF.anuario.href,
        },
    ]

    const ownSource = t(k('sources.8'), 'Cálculos de reparto y ejemplo numérico: elaboración propia de Tipi')

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(k('dek.beforeTotal'), 'Los turistas extranjeros dejaron ')}
                            <strong>{t(k('dek.total'), '18.218 millones de euros')}</strong>
                            {t(k('dek.afterTotalBeforePct'), ' en España en julio de 2026, un ')}
                            <strong>{t(k('dek.totalPct'), '10,9 %')}</strong>
                            {t(k('dek.afterPctBeforeFood'), ' más que el año pasado. De eso, ')}
                            <strong>{t(k('dek.food'), '2.723 millones')}</strong>
                            {t(k('dek.afterFoodBeforeFoodPct'), ' fueron a comer y beber fuera: un ')}
                            <strong>{t(k('dek.foodPct'), '7,4 %')}</strong>
                            {t(
                                k('dek.afterFoodPct'),
                                ' más, pero medio punto menos de cuota. Con los precios de restauración subiendo un 4,5 %, el crecimiento real se queda en un 2,8 %. Y en casa, las visitas a bares y restaurantes cayeron un 3,1 %.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'Terraza de bar de costa con ticket de caja sobre la mesa y turistas con bolsas de supermercado al fondo',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='terraza' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.terraza'), 'La terraza llena y la caja que no cuadra')}
                            </h2>
                            <p className={article.p}>
                                {t(
                                    k('intro.1'),
                                    'Bajas la persiana un sábado de julio. La terraza no ha parado y has tenido lista de espera a las dos y media.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('intro.2'), 'Y luego cuadras la caja y el número no se parece al del año pasado.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('intro.3'),
                                    'Por la mañana los has visto pasar por delante. Nevera de playa, sombrilla y dos bolsas del supermercado. Comen en el apartamento y bajan a tomar algo a las siete.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('intro.4'),
                                        'No es que haya venido menos gente. Es que la que ha venido ha decidido otra cosa.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='en-cristiano'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.enCristiano'), 'Qué dice EGATUR de julio de 2026')}
                        </h2>
                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <tbody>
                                    <tr>
                                        <th scope='row'>
                                            {t(k('table.total.label'), 'Gasto total de turistas extranjeros')}
                                        </th>
                                        <td>
                                            <strong>
                                                {t(k('table.total.value'), '18.218 millones de euros, +10,9 %')}
                                            </strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>
                                            {t(k('table.food.label'), 'Gasto en manutención (comer y beber fuera)')}
                                        </th>
                                        <td>
                                            <strong>{t(k('table.food.value'), '2.723 millones, +7,4 %')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.perTourist.label'), 'Gasto medio por turista')}</th>
                                        <td>{t(k('table.perTourist.value'), '1.579 €, +5,9 %')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.daily.label'), 'Gasto medio diario')}</th>
                                        <td>{t(k('table.daily.value'), '218 €, +3,7 %')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('table.prices.label'), 'Precios de restauración')}</th>
                                        <td>
                                            <strong>{t(k('table.prices.value'), '+4,5 %')}</strong>
                                            {t(k('table.prices.after'), ' interanual')}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('table.source.before'),
                                        'Fuente: Encuesta de Gasto Turístico del INE, julio de 2026',
                                    )}
                                    <SourceChip source='egatur' />
                                </span>
                                {t(
                                    k('table.source.and'),
                                    ', e índice de precios de restauración recogido por Hostelería de España',
                                )}
                                <span className={article.cite}>
                                    <SourceChip source='precios' />
                                </span>
                                .
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('table.punch.strong'),
                                        'El dinero del turista no ha bajado. Ha subido casi un 11 %.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('table.punch.after'),
                                    'Cualquier relato que empiece por «el turista viene sin dinero» choca de frente con la estadística oficial.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='pierdes-cuota'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cuota'), 'Pierdes cuota en el bolsillo del turista')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('cuota.p1.before'), 'La manutención fue el ')}
                                <strong>{t(k('cuota.p1.share2026'), '14,95 %')}</strong>
                                {t(
                                    k('cuota.p1.mid'),
                                    ' del gasto total del turista en julio de 2026. En julio de 2025 era el ',
                                )}
                                <strong>{t(k('cuota.p1.share2025'), '15,43 %')}</strong>
                                {t(
                                    k('cuota.p1.after'),
                                    '. Medio punto sobre 18.218 millones son casi 90 millones que se fueron a otra parte.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('cuota.p3.before'), '¿A cuál? El paquete turístico creció un ')}
                                <strong>{t(k('cuota.p3.package'), '19 %')}</strong>
                                {t(k('cuota.p3.mid1'), ', las actividades un ')}
                                <strong>{t(k('cuota.p3.activities'), '9,2 %')}</strong>
                                {t(k('cuota.p3.mid2'), ' y el alojamiento un ')}
                                <strong>{t(k('cuota.p3.lodging'), '6,7 %')}</strong>
                                {t(
                                    k('cuota.p3.after'),
                                    '. Todas por encima del 7,4 % de la comida. El turista gasta más, pero dedica menos a sentarse en una mesa.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cliente-de-casa'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.casa'), 'El que se ha ido de verdad es el cliente de casa')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('casa.p1'),
                                    'El turismo tapa el agujero dos meses. Los otros diez los paga el vecino, y el vecino ha recortado.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('casa.p2.before'),
                                        'En el primer trimestre de 2026, los actos de consumo fuera del hogar cayeron un ',
                                    )}
                                    <strong>{t(k('casa.p2.visits'), '3,1 %')}</strong>
                                    {t(k('casa.p2.mid1'), ', las consumiciones un ')}
                                    <strong>{t(k('casa.p2.consumptions'), '4,4 %')}</strong>
                                    {t(k('casa.p2.mid2'), ' y el gasto medio por persona un ')}
                                    <strong>{t(k('casa.p2.spend'), '2,5 %')}</strong>
                                    {t(
                                        k('casa.p2.after'),
                                        ', hasta 235,95 euros, según paneles recogidos por Hostelería Madrid',
                                    )}
                                    <SourceChip source='hosteleriaMadrid' />
                                </span>
                                {t(k('casa.p3.before'), '. La cerveza concentra el ')}
                                <strong>{t(k('casa.p3.beerShare'), '94,1 %')}</strong>
                                {t(k('casa.p3.mid'), ' de la pérdida en bebidas; los aperitivos caen un ')}
                                <strong>{t(k('casa.p3.aperitifs'), '7,4 %')}</strong>
                                {t(
                                    k('casa.p3.after'),
                                    '. La comida del mediodía crece un 1,6 %. Se ha roto la consumición, no el plato: justo donde estaba tu margen.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('casa.p5.before'), 'Y un competidor nuevo: ')}
                                <strong>
                                    {t(
                                        k('casa.p5.strong'),
                                        '1,3 millones de personas comen ya en el supermercado, el doble que el año anterior',
                                    )}
                                </strong>
                                {t(
                                    k('casa.p5.after'),
                                    '; casi cuatro de cada diez ocasiones nuevas de plato preparado vienen de bares y restaurantes.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='por-que-gasta-menos'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(
                                k('h2.porQue'),
                                '¿Por qué el turista gasta menos en restaurantes si viene con más dinero?',
                            )}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('porQue.p1'),
                                    'Por dónde duerme. El que se aloja en vivienda vacacional tiene cocina, y eso cambia el comportamiento entero.',
                                )}
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('porQue.p2.before'), 'En Baleares hablan de un ')}
                                    <strong>
                                        {t(
                                            k('porQue.p2.strong'),
                                            'descenso del 10 al 15 % del gasto en comercio local',
                                        )}
                                    </strong>
                                    {t(
                                        k('porQue.p2.after'),
                                        ' mientras las grandes superficies firman un verano fuerte (Menorca Info, 20 de agosto de 2026)',
                                    )}
                                    <SourceChip source='menorca' />
                                </span>
                                {t(k('porQue.p2.quoteBefore'), '. La familia en apartamento ')}
                                <em>{t(k('porQue.p2.quote'), '«consume como un local»')}</em>
                                {t(k('porQue.p2.quoteAfter'), ': compra grande, cocina y sale menos.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('porQue.p3.before'), 'Reme Cerdá (AEHTMA, Marina Alta) lo resume: ')}
                                <em>{t(k('porQue.p3.quote1'), '«Ya no se come ni se bebe tanto»')}</em>
                                <span className={article.cite}>
                                    {t(k('porQue.p3.mid'), '. Y la cifra de la década: ')}
                                    <SourceChip source='marinaAlta' />
                                </span>
                                <em>
                                    {t(
                                        k('porQue.p3.quote2'),
                                        '«hace unos años hablábamos de una rentabilidad de entre el 17-15 %; hoy si llegamos a un 3 % ó 4 % es un éxito»',
                                    )}
                                </em>
                                .
                            </p>
                        </div>
                    </section>

                    <section id='cuenta-de-la-vieja'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.cuenta'), 'Cuánto de ese récord llega a tu local')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('cuenta.p1.before'),
                                        'Reparte los 2.723 millones de manutención entre los más de 300.000 establecimientos del Anuario de Hostelería de España',
                                    )}
                                    <SourceChip source='anuario' />
                                </span>
                                {t(k('cuenta.p2.before'), ': ')}
                                <strong>{t(k('cuenta.p2.strong'), '9.077 € por establecimiento en julio')}</strong>
                                {t(k('cuenta.p2.after'), ' (~293 €/día). Solo el crecimiento (+188 millones): ')}
                                <strong>{t(k('cuenta.p3.strong'), '625 € al mes, ~20 € al día.')}</strong>{' '}
                                {t(
                                    k('cuenta.p3.after'),
                                    'Con la carta al +4,5 %, eso es el IPC y poco más. El récord de los titulares, repartido, es inflación. Aviso: el turismo se concentra en costa e islas; en Palencia, cero.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='ejemplo-margen'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.ejemplo'), 'Un ejemplo con números: la venta cae un 6 % y el margen un 20 %')}
                        </h2>
                        <div className={article.prose}>
                            <p className={cn(article.pNote, article.pGapMd)}>
                                {t(
                                    k('ejemplo.note'),
                                    'Estimación propia a partir de una estructura típica de bar de costa, no es un dato de mercado.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('ejemplo.p1.before'), 'Un local con ')}
                                <strong>{t(k('ejemplo.p1.strong'), '90 comandas al día')}</strong>
                                {t(k('ejemplo.p1.after'), ' en julio.')}
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.table}>
                                <thead>
                                    <tr>
                                        <th scope='col'>{t(k('ejemplo.table.col1'), 'Concepto')}</th>
                                        <th scope='col'>{t(k('ejemplo.table.col2'), 'Julio 2025 → Julio 2026')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>{t(k('ejemplo.table.ticket'), 'Ticket medio')}</td>
                                        <td>
                                            {t(k('ejemplo.table.ticket.before'), '24,00 € → ')}
                                            <strong>{t(k('ejemplo.table.ticket.after'), '22,50 €')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('ejemplo.table.sales'), 'Venta diaria')}</td>
                                        <td>
                                            {t(k('ejemplo.table.sales.before'), '2.160 € → ')}
                                            <strong>{t(k('ejemplo.table.sales.after'), '2.025 € (−6,2 %)')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('ejemplo.table.foodCost'), 'Materia prima')}</td>
                                        <td>
                                            {t(k('ejemplo.table.foodCost.before'), '713 € (33 %) → ')}
                                            <strong>{t(k('ejemplo.table.foodCost.after'), '749 € (37 %)')}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>{t(k('ejemplo.table.staff'), 'Personal (mismo servicio)')}</td>
                                        <td>{t(k('ejemplo.table.staff.value'), '600 € → 600 €')}</td>
                                    </tr>
                                    <tr className={article.tableTotal}>
                                        <td>
                                            <strong>{t(k('ejemplo.table.margin'), 'Margen bruto diario')}</strong>
                                        </td>
                                        <td>
                                            <strong>
                                                {t(k('ejemplo.table.margin.value'), '847 € → 676 € (−20,2 %)')}
                                            </strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('ejemplo.p2'),
                                    'Mismas 90 comandas y carta al +4,5 %: el ticket «debería» ser 25,08 €. Pero quien pedía entrante, plato, postre y vino ahora pide algo para compartir y un agua.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('ejemplo.p3'),
                                        'La venta cae un 6 % y el margen bruto un 20 %. Tres veces más.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('ejemplo.p4'),
                                    'La materia prima sube en euros aunque vendas menos: las raciones y lo de compartir casi nunca son los platos que escandallaste para tirar del margen. Misma trampa que en ',
                                )}
                                <LearnArticleLink slug='escandallo-menu-del-dia'>
                                    {t(k('ejemplo.p5.link'), 'el escandallo del menú del día')}
                                </LearnArticleLink>
                                {t(k('ejemplo.p5.after'), ': promedio perfecto y pérdida diaria.')}
                            </p>
                        </div>
                    </section>

                    <section id='esta-semana'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.estaSemana'), 'Qué hacer antes de octubre')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={weekActions} />
                        </div>
                    </section>

                    <section id='pendientes'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.pendientes'), 'Donde AECOC y el panel de consumo no cuadran')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(k('pendientes.p1.before'), 'AECOC (febrero 2026) dice que el ')}
                                    <strong>{t(k('pendientes.p1.pct'), '78 %')}</strong>
                                    {t(k('pendientes.p1.cite'), ' mantiene o aumenta gasto en hostelería')}
                                    <SourceChip source='aecoc' />
                                </span>
                                {t(
                                    k('pendientes.p1.after'),
                                    '; los paneles miden visitas a −3,1 %. Intención frente a compra. Además, la manutención de EGATUR incluye hoteles: la cuota de la hostelería independiente es menor que el 14,95 %. Marina Alta y Baleares marcan tendencia, no magnitud. Falta agosto (fin de septiembre).',
                                )}
                            </p>
                        </div>
                    </section>

                    <div className={article.prose}>
                        <p className={cn(article.p, article.pGap2xl)}>
                            {t(
                                k('closing.p1'),
                                'Si no sabes qué platos han cambiado de peso este verano ni cuánto te cuesta cada uno, cuéntanoslo: revisamos el mix y el escandallo antes de que cierre la carta de invierno.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), '¿Sabes qué platos mandan en tu food cost este verano?')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Revisamos contigo el mix de ventas y el escandallo de los platos que más han subido de peso.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        <li>{ownSource}</li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
